
import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FormHeading } from "./FormHeading";
import { UseFormReturn } from "react-hook-form";
import { useState } from "react";
import { PlusCircle, X, Check } from "lucide-react";
import { toast } from "sonner";

interface CategoryFieldProps {
  form: UseFormReturn<any>;
  categories: string[];
}

export const CategoryField: React.FC<CategoryFieldProps> = ({
  form,
  categories,
}) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newCategoryValue, setNewCategoryValue] = useState("");
  const [recentlyAdded, setRecentlyAdded] = useState<string | null>(null);
  
  const handleSelectChange = (value: string) => {
    if (value === "new-category") {
      setIsAddingNew(true);
      setNewCategoryValue("");
    } else {
      form.setValue("category", value);
      setRecentlyAdded(null);
    }
  };

  const handleNewCategorySubmit = () => {
    if (newCategoryValue.trim()) {
      // Set the form value to the new category
      form.setValue("category", newCategoryValue.trim());
      
      // Show visual feedback
      setRecentlyAdded(newCategoryValue.trim());
      
      // Show toast notification
      toast.success(`Category "${newCategoryValue.trim()}" added successfully`);
      
      // Reset the new category UI
      setIsAddingNew(false);
      setNewCategoryValue("");
    }
  };

  const handleNewCategoryCancel = () => {
    setIsAddingNew(false);
    // Set form value back to first category if available, or empty string
    const defaultCategory = categories.length > 0 ? categories[0] : "";
    form.setValue("category", defaultCategory);
    setRecentlyAdded(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleNewCategorySubmit();
    } else if (e.key === "Escape") {
      handleNewCategoryCancel();
    }
  };

  // Create a combined categories array that includes recently added category at the top if it exists
  const displayCategories = recentlyAdded && !categories.includes(recentlyAdded) 
    ? [recentlyAdded, ...categories] 
    : categories;

  return (
    <>
      <FormField
        control={form.control}
        name="category"
        render={({ field }) => (
          <FormItem>
            <FormHeading label="Category" />
            {!isAddingNew ? (
              <Select
                onValueChange={handleSelectChange}
                value={field.value || ''}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {displayCategories.map((category) => (
                    <SelectItem 
                      key={category} 
                      value={category}
                      className={category === recentlyAdded ? "text-primary font-medium flex items-center gap-2" : ""}
                    >
                      {category === recentlyAdded && (
                        <Check className="h-4 w-4 mr-1 text-primary" />
                      )}
                      {category}
                      {category === recentlyAdded && " (newly added)"}
                    </SelectItem>
                  ))}
                  <SelectItem value="new-category" className="text-primary flex items-center gap-2">
                    <PlusCircle className="h-4 w-4" />
                    Add New Category
                  </SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex space-x-2">
                <div className="flex-1">
                  <Input
                    autoFocus
                    placeholder="Enter new category name"
                    value={newCategoryValue}
                    onChange={(e) => setNewCategoryValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <Button 
                  type="button" 
                  size="sm"
                  onClick={handleNewCategorySubmit}
                  disabled={!newCategoryValue.trim()}
                >
                  Add
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  size="sm"
                  onClick={handleNewCategoryCancel}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};
